import { WithOrderNum } from "@/types/order";
import {
    ICoordinate,
    IOptionNumberItem,
    IPageData,
    IPageParams
} from "../types"
import { IModelGetData } from "./model.types";

/**
 * 设备等级
 * $t('enum.E_EquipmentLevel.A')
 * $t('enum.E_EquipmentLevel.B')
 * $t('enum.E_EquipmentLevel.C')
 */
export enum E_EquipmentLevel {
    A = 'A',
    B = 'B',
    C = 'C',
}

/**
 * 导入状态
 * $t('enum.E_ImportStatus.Success')
 * $t('enum.E_ImportStatus.Failed')
 */
export enum E_ImportStatus {
    /**
     * 成功
     */
    Success = 1,
    /**
     * 失败
     */
    Failed = 2
}

export type IGetEquipmentListByAreaIdData = WithOrderNum<IOptionNumberItem>[]

export interface IEquipmentExtend {
    /**
     * 部件属性JSON
     */
    attributeJson?: string
    /**
     * 编辑时候如果modelId没变这个需要传入
     */
    id?: number
    /**
     * 设备ID
     */
    equipmentId?: number
    /**
     * 部件ID
     */
    componentId?: number
    /**
     * 公司ID
     */
    companyId?: number
    /**
     * 区域ID
     */
    areaId?: number
    /**
     * 父公司ID
     */
    parentCompanyId?: number
    /**
     * 设备名称
     */
    equipmentName?: string
    /**
     * 设备类型ID
     */
    equipmentTypeId?: number
    /**
     * 名称
     */
    name: string
    /**
     * 皮带周长
     */
    beltGirth: number;
    /**
     * 皮带型号
     */
    beltType: number;
    /**
     * 叶片数量
     */
    bladesNum: number;
    /**
     * 从动轮直径
     */
    drivenWheelDiameter: number;
    /**
     * 主动轮直径
     */
    drivingWheelDiameter: number;
    /**
     * 模型ID
     */
    modelId: number;
    /**
     * 模型配置ID
     */
    modelConfigId: number
    /**
     * 固有频率
     */
    naturalFrequency: number;
    /**
     * 驱动端轴承库型号
     */
    driveBearingType: string;
    /**
     * 非驱动端轴承库型号
     */
    nonDriveBearingType: string;
    /**
     * 额定电流
     */
    ratedCurrent: number;
    /**
     * 额定功率
     */
    ratedPower: number;
    /**
     * 额定转速
     */
    ratedSpeed: number;
    /**
     * 齿比
     */
    toothRatio: number;
    gearRecord: IGearPairRecord[]
    /**
     * 阈值系数
     */
    thresholdCoefficient: number
    /**
     * 算法配置
     */
    diagnosisModelCode: string
    /**
     * 算法配置列表
     */
    diagnosisModelCodeList?: string[]
    ballMillAttribute?: IEquipmentBaseAttribute
    centrifugeAttribute?: IEquipmentCentrifugeAttribute
    compressorAttribute?: IEquipmentCompressorAttribute
    conveyorAttribute?: IEquipmentConveyorAttribute
    crusherAttribute?: IEquipmentBaseAttribute
    fanAttribute?: IEquipmentFanAttribute
    gearboxAttribute?: IEquipmentGearboxAttribute
    generatorAttribute?: IEquipmentGeneratorAttribute
    hydraulicTurbineAttribute?: IEquipmentHydraulicTurbineAttribute
    machineToolAttribute?: IEquipmentBaseAttribute
    motorAttribute?: IEquipmentMotorAttribute
    otherAttribute?: IEquipmentOtherAttribute
    pumpAttribute?: IEquipmentPumpAttribute
    reactorAttribute?: IEquipmentBaseAttribute
    rollerAttribute?: IEquipmentRollerAttribute
    steamTurbineAttribute?: IEquipmentSteamTurbineAttribute
    transformerAttribute?: IEquipmentTransformerAttribute
    vacuumPumpAttribute?: IEquipmentVacuumPumpAttribute
    vibratingScreenAttribute?: IEquipmentVibratingScreenAttribute
}

export interface IEquipmentBaseAttribute {
    /**
     * 额定功率
     */
    ratedPower: number
    /**
     * 额定转速
     */
    ratedSpeed: number
    /**
     * 阈值系数
     */
    thresholdCoefficient?: number
}

export interface IEquipmentOtherAttribute extends IEquipmentBaseAttribute {
    /**
     * 固有频率
     */
    naturalFrequency: number
}

export interface IEquipmentMotorAttribute extends IEquipmentOtherAttribute {
    /**
     * 额定电流
     */
    ratedCurrent: number
    /**
     * 额定电压
     */
    ratedVoltage: number
}

export interface IEquipmentCentrifugeAttribute extends IEquipmentOtherAttribute {
    /**
     * 叶片数量
     */
    bladesNum: number
    /**
     * 额定电流
     */
    ratedCurrent: number
}

export interface IEquipmentCompressorAttribute extends IEquipmentOtherAttribute {
    /**
     * 叶片数量
     */
    bladesNum: number
}

export interface IEquipmentFanAttribute extends IEquipmentOtherAttribute {
    /**
     * 叶片数量
     */
    bladesNum: number
    /**
     * 流量
     */
    flow: number
}

export interface IEquipmentPumpAttribute extends IEquipmentFanAttribute {
    /**
     * 扬程
     */
    head: number
}

export interface IEquipmentVacuumPumpAttribute extends IEquipmentFanAttribute { }

export interface IEquipmentGearboxAttribute {
    /**
     * 输入转速
     */
    inputSpeed: number
    /**
     * 输出转速
     */
    outputSpeed: number
    /**
     * 速比
     */
    speedRatio: number
    /**
     * 固有频率
     */
    naturalFrequency: number
    /**
     * 额定功率
     */
    ratedPower: number
    /**
     * 阈值系数
     */
    thresholdCoefficient?: number
}

export interface IEquipmentRollerAttribute extends IEquipmentBaseAttribute {
    /**
     * 输送速度
     */
    conveyorSpeed: number
    /**
     * 辊子直径
     */
    rollerDiameter: number
}

export interface IEquipmentConveyorAttribute extends IEquipmentRollerAttribute { }

export interface IEquipmentTransformerAttribute {
    /**
     * 适用频率
     */
    applicableFrequency: number
    /**
     * 联接方式
     */
    connectionMode: string
    /**
     * 冷却方式
     */
    coolingMode: string
    /**
     * 电流频率
     */
    currentFrequency: number
    /**
     * 输入电压
     */
    inputVoltage: number
    /**
     * 输出电压
     */
    outputVoltage: number
}

export interface IEquipmentGeneratorAttribute extends IEquipmentBaseAttribute {
    /**
     * 额定频率
     */
    ratedFrequency: number
    /**
     * 额定电压
     */
    ratedVoltage: number
}

export interface IEquipmentHydraulicTurbineAttribute extends IEquipmentBaseAttribute {
    /**
     * 流量
     */
    flow: number
    /**
     * 出力
     */
    outputPower: number
    /**
     * 水头
     */
    waterHead: number
}

export interface IEquipmentSteamTurbineAttribute extends IEquipmentBaseAttribute {
    /**
     * 临界转速
     */
    criticalSpeed: number
    /**
     * 入口蒸汽流量
     */
    inletSteamFlow: number
    /**
     * 入口蒸汽压力
     */
    inletSteamPressure: number
    /**
     * 入口蒸汽温度
     */
    inletSteamTemperature: number
    /**
     * 出口蒸汽压力
     */
    outletSteamPressure: number
    /**
     * 出口蒸汽温度
     */
    outletSteamTemperature: number
}

export interface IEquipmentVibratingScreenAttribute extends Omit<IEquipmentBaseAttribute, 'thresholdCoefficient'> {
    /**
     * 振幅
     */
    amplitude: number
    /**
     * 尺寸
     */
    dimension: string
    /**
     * 频率
     */
    frequency: number
}

export interface IGearPairRecord {
    id?: number
    /**
     * 轴号
     */
    axleId?: number
    /**
     * 兼容旧字段
     */
    axleld?: number
    equipmentId?: number
    extendId?: number
    createTime?: string
    updateTime?: string
    /**
     * 齿轮1齿数
     */
    firstNum: number
    /**
     * 齿轮1驱动端轴承型号
     */
    firstDrive: string
    /**
     * 齿轮1非驱动端轴承型号
     */
    firstNoDrive: string
    /**
     * 齿轮2齿数
     */
    secondNum: number
    /**
     * 齿轮2驱动端轴承型号
     */
    secondDrive: string,
    /**
     * 齿轮2非驱动端轴承型号
     */
    secondNoDrive: string
}

export interface IEquipmentCreateItem extends Partial<ICoordinate> {
    /**
     * 公司ID
     */
    companyId: number
    /**
     * 区域ID
     */
    areaId: number;
    /**
     * 设备等级
     */
    equipmentLevel: E_EquipmentLevel;
    /**
     * 设备类型ID
     */
    equipmentTypeId: number;
    extend: IEquipmentExtend[];
    /**
     * 部件列表
     */
    componentList?: IEquipmentComponent[]

    /**
     * 维保时间
     */
    maintenanceTime: number;

    /**
     * 运行时间
     */
    runTime?: string

    /**
     * 图片ID
     */
    imgId?: number
    /**
     * 图片URL
     */
    imgUrl?: string
    /**
     * 在线状态
     */
    online?: number
    /**
     * 排序
     */
    orderNum?: number
    /**
     * 父公司ID
     */
    parentCompanyId?: number
    /**
     * 运行状态
     */
    runStatus?: number

    /**
     * 模型ID
     */
    modelId: number;
    /**
     * 设备名称
     */
    name: string;
}

export interface IEquipmentComponent {
    areaId?: number
    companyId?: number
    gearId?: number
    id?: number
    name?: string
    parentCompanyId?: number
    pointMappingList?: IEquipmentPointMapping[]
}

export interface IEquipmentPointMapping extends Partial<ICoordinate> {
    /**
     * APEF状态：1 开启
     */
    apefStartStatus?: number
    areaId?: number
    cardChannelId?: number
    cardId?: number
    /**
     * 通信选择，1网关，2采集卡
     */
    commChoice?: number
    companyId?: number
    componentId?: number
    driveType?: number
    equipmentId?: number
    extendId?: number
    fileIds?: string
    gearId?: number
    id?: number
    parentCompanyId?: number
    pointId?: number
    pointName?: string
    /**
     * 运行状态：1 开机 2 停机 3 停用
     */
    runStatus?: number
    /**
     * 阈值
     */
    runThreshold?: number
    samplingMultiple?: number
    sensorId?: string
    slopeStartStatus?: number
    sort?: number
    /**
     * 温度阈值最大值
     */
    temRunThresholdMax?: number
    /**
     * 温度阈值最小值
     */
    temRunThresholdMin?: number
}

export interface IEquipmentEditItem extends IEquipmentCreateItem {
    id: number
}



export type IEquipmentCreateParams = IEquipmentCreateItem
export type IEquipmentEditParams = IEquipmentEditItem
export interface IEquipmentGetData extends IEquipmentEditItem {
    companyName: string
    areaName: string
    imgId: number
    imgUrl: string
}


export enum E_EquipmentTypeListType {
    Model = 0,
    Equipment = 1,
}

export interface IEquipmentTypeListParams {
    companyId: number
    type: E_EquipmentTypeListType
}

export interface IEquipmentTypeListItem extends IOptionNumberItem {
    typeCount: number
}

export type IEquipmentTypeListData = IEquipmentTypeListItem[]

export interface IEquipmentListItem extends ICoordinate {

    // 区域图片地址
    areaImgUrl: string

    companyId: number

    areaId: number;
    /**
     * 区域
     */
    areaName: string;
    createBy: null;
    createTime: string;
    delFlag: number;
    /**
     * 设备等级
     */
    equipmentLevel: string;
    /**
     * 设备名称
     */
    equipmentName: string;
    /**
     * 设备类型Id
     */
    equipmentTypeId: number;
    /**
     * 设备类型名称
     */
    equipmentTypeName: null;
    /**
     * 设备ID
     */
    id: number;
    /**
     * 维保时间
     */
    maintenanceTime: number;
    /**
     * 测点数量
     */
    mappingCount: number;
    /**
     * 模型ID
     */
    modelId: string;
    /**
     * 设备模型
     */
    modelName: null;
    /**
     * 设备名称
     */
    name: string;
    updateBy: null;
    updateTime: null;

    orderNum: number
}

export interface IEquipmentListParams extends IPageParams {
    areaId?: number
    companyId?: number
    equipmentTypeIdList?: number[]
    name?: string
}

export type IEquipmentListData = IPageData<IEquipmentListItem>



export interface IEquipmentTypeModelListItem {
    modelList: IModelGetData[]
}

export interface IEquipmentTypeCheckParams {
    companyId: number
    name: string
}

export interface IEquipmentImportParams {
    file: File
}

/**
 * 皮带
 */
export interface IBeltItem {
    beltGirth: string
    beltType: string
    drivenWheelDiameter: string
    drivingWheelDiameter: string
    equipmentName: string
}

/**
 * 电机、空压机、离心机、辊子类别
 */
export interface ICentrifugeCompressorCurrentRollerItem {
    driveBearingType: string
    equipmentName: string
    naturalFrequency: string
    nonDriveBearingType: string
    ratedCurrent: string
    ratedPower: string
    ratedSpeed: string
}

/**
 * 风机、水泵类别
 */
export interface IFanPumpItem {
    bladesNum: string
    driveBearingType: string
    equipmentName: string
    naturalFrequency: string
    nonDriveBearingType: string
    ratedPower: string
    ratedSpeed: string
}

/**
 * 齿轮箱
 */
export interface IGearItem {
    adrive: string
    anodrive: string
    anum: string
    bdrive: string
    bnodrive: string
    bnum: string
    cdrive: string
    cnodrive: string
    cnum: string
    ddrive: string
    dnodrive: string
    dnum: string
    edrive: string
    eenum: string
    enodrive: string
    equipmentName: string
    fdrive: string
    fnodrive: string
    fnum: string
    gdrive: string
    gnodrive: string
    gnum: string
    hdrive: string
    hnodrive: string
    hnum: string
    naturalFrequency: string
    ratedPower: string
    ratedSpeed: string
}

export interface IEquipmentImportItem {
    /**
     * 皮带
     */
    beltList: IBeltItem[]
    /**
     * 离心机
     */
    centrifugeList: ICentrifugeCompressorCurrentRollerItem[]
    /**
     * 空压机
     */
    compressorList: ICentrifugeCompressorCurrentRollerItem[]
    /**
     * 电机
     */
    currentList: ICentrifugeCompressorCurrentRollerItem[]
    /**
     * 风机
     */
    fanList: IFanPumpItem[]
    /**
     * 齿轮箱
     */
    gearList: IGearItem[]

    /**
     * 水泵
     */
    pumpList: IFanPumpItem[]
    /**
     * 辊子
     */
    rollerList: ICentrifugeCompressorCurrentRollerItem[]

    /**
     * 模型
     */
    modelList: number[]

    /**
     * 区域名称
     */
    areaName: string
    companyName: string
    equipmentId: number
    equipmentLevel: E_EquipmentLevel
    equipmentName: string
    equipmentType: string
    equipmentTypeId: number
    /**
     * 维保时间
     */
    maintenanceTime: string
    /**
     * 模型id
     */
    modelId: string
    /**
     * 设备名模型
     */
    modelName: string
    /**
     * 测点数量
     */
    pointNum: string
}

export type IEquipmentImportData = IEquipmentImportItem[]
export interface IEquipmentRepeatAddParams {

    repeatVOS: IEquipmentImportItem[]

}

export interface IEquipmentSortingItem {
    id: number
    orderNum: number
}

export type IEquipmentSortingParams = IEquipmentSortingItem[]


export type IEquipmentListAllData = IEquipmentListItem[]

export interface IEquipmentListSelectParams {
    companyId?: number
    areaId?: number
    name?: string
}

export interface IEquipmentUploadImageParams {
    /**
     * 设备id
     */
    equipmentId: number
    /**
     * 图片ID
     */
    imgId: number
}

export interface IEquipmentGetMonitorImgParams {
    mappingId: number
}

export interface IEquipmentGetMonitorImgData {
    equipmentId: number
    imgId: number
    imgUrl: string
}

export interface IEquipmentSettingCoordinatesParams extends ICoordinate {
    equipmentId: number
}


export interface IEquipmentSensorImportParams {
    file: File
}

export interface IEquipmentSensorImportData {
    /**
     * 错误数据条数
     */
    errorCount: number
    /**
     * base64数据
     */
    errorExcelData: string
    /**
     * 文件名称
     */
    errorFileName: string
    /**
     * 导入状态
     */
    importStatus: number
}
